import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { AppShell } from "./components/AppShell";
import { AuthScreen } from "./components/AuthScreen";
import { AccountPage } from "./pages/AccountPage";
import { AdminAccessPage } from "./pages/AdminAccessPage";
import { BonusesPage } from "./pages/BonusesPage";
import { ResellerRightsPage } from "./pages/ResellerRightsPage";
import { HelpPage } from "./pages/ReferencePages";
import { StudioDashboard } from "./pages/StudioDashboard";
import { ProjectStudio } from "./pages/StudioPage";
import { usePortal } from "./state/PortalContext";

function HomeRoute() {
  const { authStatus, session } = usePortal();

  if (authStatus === "loading") {
    return <main className="account-boot" aria-label="Opening your account" />;
  }

  return <Navigate to={session ? "/account" : "/login"} replace />;
}

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <main className="account-not-found">
      <div>
        <span>404</span>
        <h1>We could not find that page.</h1>
        <p>Check the address, or return to the Paper Kingdom home page.</p>
        <a className="account-button" href="/paperkingdom">
          Go to Paper Kingdom
        </a>
      </div>
    </main>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: AuthScreen,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: AuthScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: AuthScreen,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: AuthScreen,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminAccessPage,
});

const paperKingdomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/PaperKingdom",
  component: AppShell,
});

const paperKingdomIndexRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/",
  component: () => <Navigate to="/PaperKingdom/studio" replace />,
});

const studioRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/studio",
  component: StudioDashboard,
});

const projectRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/studio/projects/$projectId",
  component: () => {
    const { projectId } = projectRoute.useParams();
    return <ProjectStudio projectId={projectId} />;
  },
});

const helpRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/help",
  component: HelpPage,
});

const bonusesRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/bonuses",
  component: BonusesPage,
});

const resellerRightsRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/reseller-rights",
  component: ResellerRightsPage,
});

const productLoginRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/login",
  component: () => <Navigate to="/login" replace />,
});

const productRegisterRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/register",
  component: () => <Navigate to="/register" replace />,
});

const productForgotPasswordRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/forgot-password",
  component: () => <Navigate to="/forgot-password" replace />,
});

const productResetPasswordRoute = createRoute({
  getParentRoute: () => paperKingdomRoute,
  path: "/reset-password",
  component: () => <Navigate to="/reset-password" replace />,
});

const legacyStudioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio",
  component: () => <Navigate to="/PaperKingdom/studio" replace />,
});

const legacyProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/projects/$projectId",
  component: () => {
    const { projectId } = legacyProjectRoute.useParams();
    return (
      <Navigate
        to="/PaperKingdom/studio/projects/$projectId"
        params={{ projectId }}
        replace
      />
    );
  },
});

const legacyHelpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  component: () => <Navigate to="/PaperKingdom/help" replace />,
});

const legacyAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apps/paper-kingdom",
  component: () => <Navigate to="/PaperKingdom/studio" replace />,
});

const legacyAppProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apps/paper-kingdom/projects/$projectId",
  component: () => {
    const { projectId } = legacyAppProjectRoute.useParams();
    return (
      <Navigate
        to="/PaperKingdom/studio/projects/$projectId"
        params={{ projectId }}
        replace
      />
    );
  },
});

const legacyProductRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/paper-kingdom",
  component: () => <Navigate to="/PaperKingdom/studio" replace />,
});

const legacyLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library",
  component: () => <Navigate to="/PaperKingdom/studio" replace />,
});

const paperKingdomTree = paperKingdomRoute.addChildren([
  paperKingdomIndexRoute,
  studioRoute,
  projectRoute,
  bonusesRoute,
  resellerRightsRoute,
  helpRoute,
  productLoginRoute,
  productRegisterRoute,
  productForgotPasswordRoute,
  productResetPasswordRoute,
]);

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  accountRoute,
  adminRoute,
  paperKingdomTree,
  legacyStudioRoute,
  legacyProjectRoute,
  legacyHelpRoute,
  legacyAppRoute,
  legacyAppProjectRoute,
  legacyProductRoute,
  legacyLibraryRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
